import React, { useState } from 'react';
import Modal from 'react-modal';

import BoxBody from 'components/BoxBody';
import { ConfirmDeleteDialog } from 'coaching/components/CoachingCoordinatorView/ConfirmDeleteDialog';
import CoachingConstants from 'coaching/CoachingConstants';
import {
  CoachingSessionProps,
  SessionRowHeader,
  SessionRowMetadata,
  useCoachingSessionState,
} from 'coaching/components/CoachingCoordinatorView/SharedCoordinatorSessionRowComponents';
import EventTracking from 'lib/EventTracking';
import Icon from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import { CoachingInstanceGQL as CoachingInstanceType } from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { formatDateForCordinator } from 'lib/dateTimeFormat';

type Props = CoachingSessionProps & {
  isPastEvent: boolean;
};

type DeleteActionProps = {
  children: React.ReactElement;
  hasMultipleInstances: boolean;
  isInPast: boolean;
  isRowHeader: boolean;
  instance?: CoachingInstanceType;
};

// helper functions
const trackEvent = (event: string) => {
  EventTracking.trackKey(`coaching.coordinator_tool.participant_sidebar.${event}`);
};

const DeleteActionWrapper = ({
  children,
  hasMultipleInstances,
  isInPast,
  isRowHeader,
  instance,
}: DeleteActionProps) => {
  const {
    confirmDeleteDialog,
    setConfirmDeleteDialog,
  } = useCoachingSessionState();
  // We shouldn't show the session-level delete button if it's in the past and there are multiple instances
  const shouldHideDeleteButton = isInPast && hasMultipleInstances;
  const handleDeleteAction = () => {
    trackEvent(instance ? 'remove_session_instance.click' : 'remove_session.click');

    setConfirmDeleteDialog({
      ongoingSeries: hasMultipleInstances,
      instanceDateString: instance?.startDateTime
        ? formatDateForCordinator(instance.startDateTime, 'ddd, MMM D')
        : undefined,
      instanceStartDateTime: instance?.startDateTime,
      coachingSessionInstanceUuid: instance?.eventId,
    });
    EventTracking.trackKey('coaching.coordinator_tool.remove_session_confirm_dialog.view');
  };

  return shouldHideDeleteButton ? (
    <>{children}</>
  ) : (
    <Link
      data-test={`delete-action${isRowHeader ? '-rowHeader' : ''}`}
      color="chalkboard"
      noHover
      onClick={handleDeleteAction}>
      <MediaObject figure="right" center>
        {children}
        <Icon className="CoachingSessionRow-delete" name="x" size="small" />
      </MediaObject>
    </Link>
  );
};

export const CoachingSessionRow = ({
  session,
  participantUuid,
  userName,
  userType,
  isPastEvent,
}: Props) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const {
    confirmDeleteDialog,
  } = useCoachingSessionState();

  const renderInstances = () => {
    const { instances } = session;
    if (instances.length < 2) {
      return null;
    }

    const numberOfInstancesToShow = pageNumber * CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH;
    const instancesToShow = instances.slice(0, numberOfInstancesToShow);
    return (
      <Row size="xsmall">
        <BoxBody size="xxsmall">
          {instancesToShow.map(instance => {
            const dateString = formatDateForCordinator(instance.startDateTime, 'ddd, MMM D');
            return (
              <div
                className="CoachingSessionRow-instance"
                key={instance.eventId}
                data-test="coaching-session-instance">
                <DeleteActionWrapper
                  hasMultipleInstances={false}
                  isInPast={isPastEvent}
                  isRowHeader={false}
                  instance={instance}>
                  <Text element="div">{dateString}</Text>
                </DeleteActionWrapper>
              </div>
            );
          })}
          {pageNumber === 1 && instances.length > CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH && (
            <Text className="CoachingSessionRow-showMore" element="div" weight="semibold">
              <Link
                onClick={() => {
                  trackEvent('session_show_more_instances.click');
                  setPageNumber(2);
                }}>
                Show more
              </Link>
            </Text>
          )}
        </BoxBody>
      </Row>
    );
  };

  const instanceToDelete =
    isPastEvent && session.instances.length === 1 ? session.instances[0] : undefined;

  return (
    <>
      <Modal data-test="confirm-delete-modal" isOpen={!!confirmDeleteDialog}>
        <ConfirmDeleteDialog {...{ session, participantUuid, userName, userType }} />
      </Modal>
      <div className="CoachingSessionRow">
        <BoxBody size="xxsmall" className="CoachingSessionRow-header">
          <DeleteActionWrapper
            hasMultipleInstances={session.instances.length > 1}
            isInPast={isPastEvent}
            isRowHeader
            instance={instanceToDelete}>
            <div>
              <SessionRowHeader session={session} />
              <SessionRowMetadata session={session} />
            </div>
          </DeleteActionWrapper>
        </BoxBody>
        {renderInstances()}
      </div>
      </>
  );
};

export default CoachingSessionRow;
